import {
  artworkRequired,
  isArtworkDelivered,
  hasActiveArtworkServices,
  getAvailableDeliveryToolsFromMRC as getAvailableDeliveryTools,
  DELIVERY_TOOLS,
  SERVICES,
  hasActiveArtworkServiceOfTypes,
} from '@printdeal/business-logic';
import { stringArrayToObject } from '@printdeal/transformers';
import { SanitizedLineItem, SanitizedOrder } from '../../../../../../types/order/Order';

import useDeliveryTools from '../../../../../../hooks/useDeliveryTools';

import { formatAttributes } from '../../../../../../helpers/MerchantRulesHelper';
import { isQuoteOfType } from '../../../../../../helpers/LineItems';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import { Environment } from '../../../../../../helpers/env';

export const ReasonsDisabled = {
  ARTWORK_AVAILABLE: 'The required artwork has already been delivered',
  ARTWORK_UNNECESSARY: 'The product does not require artwork',
  ARTWORK_SERVICE_REQUESTED: 'The customer has requested an artwork service',
  DELIVERY_TOOLS_EMPTY: 'There are no valid delivery tools available',
  LOADING_DELIVERY_TOOLS: 'The delivery tools are being loaded',
};

const alternativeUpload = {
  enabled: true,
  deliveryMethods: ['alternative-upload'],
  deliveryToolSettings: {},
  reason: '',
};

const needsDeliveryTools = (lineItem: SanitizedLineItem, order: SanitizedOrder) => artworkRequired(lineItem)
  && !isArtworkDelivered(lineItem)
  && !isQuoteOfType(lineItem, 'hybrid')
  && !isQuoteOfType(lineItem, 'bespoke')
  && (!hasActiveArtworkServices(lineItem.id, order, Environment)
    || hasActiveArtworkServiceOfTypes(lineItem.id, order, [SERVICES.FILE_CHECK], Environment));

const getDisabledConfigurationPayload = (
  reason?: string,
) => ({
  enabled: false,
  deliveryMethods: [],
  deliveryToolSettings: {},
  reason,
});

const useUploadFiles = (lineItem: SanitizedLineItem, order: SanitizedOrder) => {
  let deliveryMethods: string[] = [];

  const { productKey, userSelection, editorType: selectedDeliveryTool } = lineItem.custom.fields;
  const attributes = formatAttributes(stringArrayToObject(userSelection));

  // The useDeliveryTools API-call will not be done unless needsDeliveryTools() is true.
  const deliveryTools = useDeliveryTools(
    productKey,
    attributes,
    needsDeliveryTools(lineItem, order),
  );

  if (!artworkRequired(lineItem)) {
    // Some products cannot be printed on.
    return getDisabledConfigurationPayload(ReasonsDisabled.ARTWORK_UNNECESSARY);
  }

  if (isArtworkDelivered(lineItem)) {
    // The files have already been delivered.
    return getDisabledConfigurationPayload(ReasonsDisabled.ARTWORK_AVAILABLE);
  }

  if (isQuoteOfType(lineItem, 'hybrid') || isQuoteOfType(lineItem, 'bespoke')) {
    // Hybrid & bespoke quotes are lineItems diverging from what's normally possible.
    // They must thus use 'alternative upload'.
    return alternativeUpload;
  }

  if (hasActiveArtworkServices(lineItem.id, order, Environment)
    && !hasActiveArtworkServiceOfTypes(lineItem.id, order, [SERVICES.FILE_CHECK], Environment)) {
    // The customer has asked for an art service, so they cannot deliver files themselves.
    return getDisabledConfigurationPayload(ReasonsDisabled.ARTWORK_SERVICE_REQUESTED);
  }

  // The customer still needs to deliver files themselves.
  if (deliveryTools.isLoading) {
    // While the tools are being fetched, display an appropriate visual.
    return getDisabledConfigurationPayload(ReasonsDisabled.LOADING_DELIVERY_TOOLS);
  }

  if (deliveryTools.isError || !deliveryTools.data) {
    dispatchAccountEvent('order_detail_api_failure', 'get_delivery_tool_settings');

    // If the tools could not be retrieved, the customer can only use 'alternative upload'.
    return alternativeUpload;
  }

  // Determine which deliveryTools are valid for the given lineItem.
  const deliveryToolSettings = deliveryTools.data;
  const validDeliveryTools = getAvailableDeliveryTools(deliveryToolSettings);

  if (validDeliveryTools.includes(DELIVERY_TOOLS.UPLOADER_TOOL)) {
    deliveryMethods.push(DELIVERY_TOOLS.UPLOADER_TOOL);
  }

  if (validDeliveryTools.includes(DELIVERY_TOOLS.PRINTQ)
    || validDeliveryTools.includes(DELIVERY_TOOLS.DESIGN_AND_PRINT)) {
    deliveryMethods.push('editor');
  }

  if (validDeliveryTools.includes(DELIVERY_TOOLS.ALTERNATIVE_UPLOAD)) {
    deliveryMethods.push('alternative-upload');
  }

  // If the customer selected a tool while adding the lineItem to the cart, limit the tool availability to said tool.
  deliveryMethods = (selectedDeliveryTool && deliveryMethods.includes(selectedDeliveryTool))
    ? [selectedDeliveryTool]
    : deliveryMethods;

  if (deliveryMethods.length > 0) {
    return {
      enabled: true,
      deliveryMethods: [...new Set(deliveryMethods)], // remove duplicates
      deliveryToolSettings,
      reason: '',
    };
  }

  // If there are no valid tools, the customer can only use 'alternative upload'
  return alternativeUpload;
};

export default useUploadFiles;
